/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Users, Shield, FileText, GraduationCap, Archive, RefreshCw,
  Edit, Trash2, Eye, EyeOff, Search, Filter, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, AlertTriangle, Building2, CreditCard,
  History, Download, Upload, Plus, Settings, Crown, LayoutDashboard,
  BookOpen, FolderOpen, BarChart3
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { getToken, getAuthHeader as getStoredAuthHeader } from '../services/tokenService';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useSuperAdminCMS() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [auditTrail, setAuditTrail] = useState([]);

  const getAuthHeader = () => {
    const token = getToken();
    return { Authorization: `Bearer ${token}` };
  };

  // Charger les statistiques du dashboard
  const loadDashboardStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/super-admin-cms/dashboard-stats`,
        { headers: getAuthHeader() }
      );
      setStats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  // Charger les données selon l'onglet actif
  const loadData = async (tab = activeTab, page = 1) => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (tab) {
        case 'membres':
          endpoint = `/api/super-admin-cms/membres?page=${page}&include_archived=${showArchived}&search=${search}`;
          break;
        case 'coopers':
          endpoint = `/api/super-admin-cms/coopers?page=${page}&include_archived=${showArchived}&search=${search}`;
          break;
        case 'souscriptions':
          endpoint = `/api/super-admin-cms/souscriptions?page=${page}&include_archived=${showArchived}&search=${search}`;
          break;
        case 'formations':
          endpoint = `/api/super-admin-cms/formations/modules`;
          break;
        case 'packs':
          endpoint = `/api/super-admin-cms/formations/packs`;
          break;
        case 'audit':
          endpoint = `/api/super-admin-cms/audit-trail?page=${page}`;
          break;
        default:
          return;
      }

      const response = await axios.get(`${BACKEND_URL}${endpoint}`, { headers: getAuthHeader() });
      
      if (tab === 'audit') {
        setAuditTrail(response.data.items || []);
      } else {
        setData(response.data.items || response.data);
      }
      
      if (response.data.pages) {
        setPagination({
          page: response.data.page || 1,
          pages: response.data.pages || 1,
          total: response.data.total || 0
        });
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès non autorisé - Super Admin requis');
        navigate('/admin/dashboard');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardStats();
  }, []);

  useEffect(() => {
    if (activeTab !== 'dashboard') {
      loadData(activeTab);
    }
    setSelectedIds([]);
  }, [activeTab, showArchived]);

  // Actions
  const handleArchive = async (id) => {
    if (!window.confirm('Voulez-vous archiver cet élément ?')) return;
    
    try {
      const endpoint = activeTab === 'souscriptions' 
        ? `/api/super-admin-cms/souscriptions/${id}/archive`
        : activeTab === 'coopers'
        ? `/api/super-admin-cms/coopers/${id}/archive`
        : `/api/super-admin-cms/membres/${id}/archive`;
        
      await axios.post(`${BACKEND_URL}${endpoint}`, { reason: 'Archivé par Super Admin' }, { headers: getAuthHeader() });
      toast.success('Élément archivé avec succès');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleRestore = async (id) => {
    try {
      const endpoint = activeTab === 'membres' 
        ? `/api/super-admin-cms/membres/${id}/restore`
        : `/api/super-admin-cms/${activeTab}/${id}/restore`;
        
      await axios.post(`${BACKEND_URL}${endpoint}`, {}, { headers: getAuthHeader() });
      toast.success('Élément restauré avec succès');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la restauration');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ ATTENTION: Cette action est irréversible. Supprimer définitivement ?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/super-admin-cms/bulk-action?entity_type=${activeTab}`,
        { ids: [id], action: 'delete' },
        { headers: getAuthHeader() }
      );
      toast.success('Élément supprimé définitivement');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.warning('Sélectionnez au moins un élément');
      return;
    }
    
    const actionLabels = {
      archive: 'archiver',
      restore: 'restaurer',
      delete: 'supprimer définitivement',
      validate: 'valider',
      reject: 'rejeter'
    };
    
    if (!window.confirm(`Voulez-vous ${actionLabels[action]} ${selectedIds.length} élément(s) ?`)) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/super-admin-cms/bulk-action?entity_type=${activeTab}`,
        { ids: selectedIds, action },
        { headers: getAuthHeader() }
      );
      toast.success(`${selectedIds.length} élément(s) traité(s) avec succès`);
      setSelectedIds([]);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'action en masse');
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const endpoint = activeTab === 'souscriptions'
        ? `/api/super-admin-cms/souscriptions/${editItem.id}`
        : activeTab === 'coopers'
        ? `/api/super-admin-cms/coopers/${editItem.id}`
        : activeTab === 'formations'
        ? `/api/super-admin-cms/formations/modules/${editItem.id}`
        : `/api/super-admin-cms/membres/${editItem.id}`;

      await axios.put(`${BACKEND_URL}${endpoint}`, editItem, { headers: getAuthHeader() });
      toast.success('Modifications enregistrées');
      setEditItem(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Colonnes pour chaque type de données
  const membresColumns = [
    { header: 'Nom', key: 'nom', render: (item) => `${item.nom || ''} ${item.prenom || ''}`.trim() || item.entreprise || item.numero_membre || '-' },
    { header: 'Email', key: 'email', render: (item) => item.email || '-' },
    { 
      header: 'Statut', 
      key: 'status',
      render: (item) => {
        const s = item.statut || item.status;
        return (
          <Badge className={
            s === 'actif' ? 'bg-green-700 text-white' :
            ['en_attente', 'en_attente_connexion', 'pending'].includes(s) ? 'bg-yellow-600 text-white' :
            s === 'incomplete' ? 'bg-blue-600 text-white' :
            'bg-gray-600 text-white'
          }>
            {s || 'N/A'}
          </Badge>
        );
      }
    },
    { header: 'Entreprise', key: 'entreprise', render: (item) => item.entreprise || '-' },
    { 
      header: 'Archivé',
      key: 'archived',
      render: (item) => item.archived ? <Archive className="w-4 h-4 text-orange-500" /> : null
    }
  ];

  const coopersColumns = [
    { header: 'Raison sociale', key: 'raison_sociale', render: (item) => item.raison_sociale || item.nom },
    { header: 'Email', key: 'email' },
    { header: 'SIRET', key: 'siret' },
    { 
      header: 'Statut', 
      key: 'statut',
      render: (item) => (
        <Badge className={
          item.statut === 'actif' ? 'bg-green-700 text-white' :
          item.statut === 'en_attente' ? 'bg-yellow-600 text-white' :
          'bg-gray-600 text-white'
        }>
          {item.statut || 'N/A'}
        </Badge>
      )
    },
    { 
      header: 'Archivé',
      key: 'archived',
      render: (item) => item.archived ? <Archive className="w-4 h-4 text-orange-500" /> : null
    }
  ];

  const souscriptionsColumns = [
    { header: 'Réf.Solidaire', key: 'numero_police' },
    { header: 'Entreprise', key: 'entreprise', render: (item) => item.entreprise?.raison_sociale || 'N/A' },
    { 
      header: 'Statut', 
      key: 'statut',
      render: (item) => (
        <Badge className={
          item.statut === 'active' ? 'bg-green-700 text-white' :
          item.statut === 'en_attente_paiement' ? 'bg-yellow-600 text-white' :
          'bg-gray-600 text-white'
        }>
          {item.statut || 'N/A'}
        </Badge>
      )
    },
    { header: 'Cotisation', key: 'cotisation_annuelle', render: (item) => `${item.cotisation_annuelle || 0} €` },
    { 
      header: 'Archivé',
      key: 'archived',
      render: (item) => item.archived ? <Archive className="w-4 h-4 text-orange-500" /> : null
    }
  ];

  const formationsColumns = [
    { header: 'Titre', key: 'title' },
    { header: 'Catégorie', key: 'category' },
    { header: 'Crédits', key: 'credits_required' },
    { header: 'Durée', key: 'duration', render: (item) => `${item.duration || 0} min` },
    { 
      header: 'Actif', 
      key: 'is_active',
      render: (item) => item.is_active ? 
        <CheckCircle className="w-4 h-4 text-green-500" /> : 
        <XCircle className="w-4 h-4 text-gray-400" />
    }
  ];

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'membres', label: 'Membres', icon: Users },
    { id: 'coopers', label: 'COOPER\'S', icon: Shield },
    { id: 'souscriptions', label: 'Souscriptions', icon: FileText },
    { id: 'formations', label: 'Formations', icon: GraduationCap },
    { id: 'packs', label: 'Packs Crédits', icon: CreditCard },
    { id: 'audit', label: 'Historique', icon: History }
  ];

  const getColumns = () => {
    switch (activeTab) {
      case 'membres': return membresColumns;
      case 'coopers': return coopersColumns;
      case 'souscriptions': return souscriptionsColumns;
      case 'formations': return formationsColumns;
      case 'packs': return formationsColumns;
      default: return [];
    }
  };

  return {
    activeTab, auditTrail, data, editItem, getColumns, handleArchive,
    handleBulkAction, handleDelete, handleRestore, handleSaveEdit, loadData, loading,
    navigate, pagination, saving, search, selectedIds, setActiveTab,
    setEditItem, setSearch, setSelectedIds, setShowArchived, showArchived, stats,
    tabs,
  };
}
